import { Badge, Box, Button, Group, ScrollArea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useRouter } from '@tanstack/react-router';
import { RowSelectionState } from '@tanstack/table-core';
import clsx from 'clsx';
import { useAtom } from 'jotai';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../modal/modal';
import OrganisationsTable, {
  OrganisationsTableProps,
} from '../organisations-table/organisations-table';

import classes from './organisation-selector.module.css';

import { organisationAtom } from '@data-access';

type RenderBottomToolbarCustomActions = NonNullable<
  NonNullable<
    OrganisationsTableProps['options']
  >['renderBottomToolbarCustomActions']
>;

export function OrganisationSelector() {
  const { t } = useTranslation();
  const [opened, actions] = useDisclosure();
  const [organisations, setOrganisations] = useAtom(organisationAtom);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const router = useRouter();

  const renderActions: RenderBottomToolbarCustomActions = useCallback(
    ({ table }) => (
      <Group>
        <Button
          onClick={() => {
            const ids = Object.keys(rowSelection).map((id) => Number(id));
            setOrganisations(ids);
            actions.close();

            router.navigate({
              to: '/dashboard',
            });
          }}
        >
          {t('common.confirm')}
        </Button>

        <Button color="red" onClick={actions.close}>
          {t('common.cancel')}
        </Button>

        <Button
          onClick={() => {
            setOrganisations([]);
            setRowSelection({});
            actions.close();

            router.navigate({
              to: '/dashboard',
            });
          }}
          variant="subtle"
        >
          {t('common.reset')}
        </Button>
      </Group>
    ),
    [actions, rowSelection, setOrganisations, t, router],
  );

  return (
    <>
      <Button
        color="pf-green"
        onClick={actions.open}
        rightSection={
          <Badge
            color={organisations.length > 0 ? 'orange' : 'pf-green'}
            variant="light"
          >
            {organisations.length}
          </Badge>
        }
        variant="white"
      >
        {t('common.organisation')}
      </Button>
      <Modal
        classNames={{
          root: clsx(classes.modal),
          content: clsx(classes.modalContent),
          header: clsx(classes.modalHeader),
          title: clsx(classes.modalTitle),
          body: clsx(classes.modalBody),
        }}
        onClose={actions.close}
        opened={opened}
        size="80%"
        title={t('common.search_organisation')}
      >
        <Box w="100%">
          <OrganisationsTable
            options={{
              enableFullScreenToggle: false,

              renderBottomToolbarCustomActions: renderActions,
              enableRowSelection: true,
              enableMultiRowSelection: false,
              onRowSelectionChange: setRowSelection,
              initialState: {
                rowSelection: organisations.reduce(
                  (acc, id) => {
                    acc[id] = true;
                    return acc;
                  },
                  {} as Record<number, boolean>,
                ),
              },
            }}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            withActions={false}
          />
        </Box>
      </Modal>
    </>
  );
}

export default OrganisationSelector;
